import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import messages from '../../statics/messages.json';
import viewIcon from '../../statics/images/view.png';
import OrderContext from '../../context/OrderContext';

const SalesOrdersArchive = () => {
    const navigate = useNavigate();
    const { fetchArchivedOrders, updateArchiveOrder } = OrderContext();

    // Data
    const [data, setData] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Pagination
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    // Sorting
    const [sortCol, setSortCol] = useState('');
    const [sortDir, setSortDir] = useState('descending');

    // Draft text states — controlled inputs, no API trigger
    const [search, setSearch] = useState('');
    const [orderSearch, setOrderSearch] = useState('');
    const [poSearch, setPoSearch] = useState('');
    const [customerSearch, setCustomerSearch] = useState('');
    const [partName, setPartName] = useState('');
    const [statusSearch, setStatusSearch] = useState('');
    const [shipMethodSearch, setShipMethodSearch] = useState('');
    const [countrySearch, setCountrySearch] = useState('');

    // Applied text state — only updates on Search button / Enter
    const [applied, setApplied] = useState({
        search: '', orderSearch: '', poSearch: '', customerSearch: '',
        partName: '', statusSearch: '', shipMethodSearch: '', countrySearch: '',
    });

    // Date filters — apply immediately on change
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [showFilters, setShowFilters] = useState(false);

    // Unarchive
    const [unarchivingIds, setUnarchivingIds] = useState(new Set());
    const [toast, setToast] = useState(null); // { type: 'success'|'error', message }

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 3500);
        return () => clearTimeout(t);
    }, [toast]);

    const handleUnarchive = async (line) => {
        const orderNum = line.order?.ordernum;
        if (!orderNum) return;
        setUnarchivingIds(prev => new Set(prev).add(orderNum));
        const result = await updateArchiveOrder(orderNum, false);
        setUnarchivingIds(prev => { const s = new Set(prev); s.delete(orderNum); return s; });
        if (result?.success) {
            setToast({ type: 'success', message: `Order #${orderNum} unarchived successfully.` });
            // Remove all rows belonging to this order from the current page
            setData(prev => prev.filter(l => l.order?.ordernum !== orderNum));
            setTotalElements(prev => Math.max(0, prev - 1));
        } else {
            setToast({ type: 'error', message: result?.message || `Failed to unarchive order #${orderNum}.` });
        }
    };

    const hasActiveFilters =
        applied.search || applied.orderSearch || applied.poSearch || applied.customerSearch ||
        applied.partName || applied.statusSearch || applied.shipMethodSearch || applied.countrySearch ||
        startDate || endDate;

    // API fetch — triggered by applied text filters, pagination, sort, dates
    useEffect(() => {
        const run = async () => {
            setLoading(true);
            setError(null);
            const resp = await fetchArchivedOrders(
                (page - 1) * perPage, perPage,
                applied.search, sortCol, sortDir,
                startDate, endDate,
                applied.orderSearch, applied.poSearch, applied.partName,
                applied.statusSearch, applied.shipMethodSearch,
                applied.customerSearch, applied.countrySearch
            );
            if (resp) {
                setData(resp.content || []);
                setTotalElements(resp.totalElements || 0);
                setTotalPages(resp.totalPages || 0);
            } else {
                setError('Failed to load archived orders. Please try again.');
            }
            setLoading(false);
        };
        run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, sortCol, sortDir, startDate, endDate, applied]);

    // Commit draft text values → triggers the useEffect above
    const applySearch = () => {
        setPage(1);
        setApplied({ search, orderSearch, poSearch, customerSearch, partName, statusSearch, shipMethodSearch, countrySearch });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') applySearch();
    };

    const clearFilters = () => {
        setSearch(''); setOrderSearch(''); setPoSearch(''); setCustomerSearch('');
        setPartName(''); setStatusSearch(''); setShipMethodSearch(''); setCountrySearch('');
        setStartDate(''); setEndDate('');
        setSortCol(''); setSortDir('descending');
        setPage(1);
        setApplied({ search: '', orderSearch: '', poSearch: '', customerSearch: '', partName: '', statusSearch: '', shipMethodSearch: '', countrySearch: '' });
    };

    const handleSort = (col) => {
        if (sortCol === col) {
            setSortDir(d => d === 'ascending' ? 'descending' : 'ascending');
        } else {
            setSortCol(col);
            setSortDir('ascending');
        }
        setPage(1);
    };

    const getRowClass = (date) => {
        if (!date) return '';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const target = new Date(date);
        target.setHours(0, 0, 0, 0);
        const diff = Math.ceil((target - today) / 86400000);
        if (diff <= 0) return 'table-danger';
        if (diff === 1) return 'table-warning';
        if (diff === 2) return 'table-info';
        return '';
    };

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <i className="fa fa-sort text-muted ms-1" style={{ fontSize: '11px' }} />;
        return sortDir === 'ascending' ? <i className="fa fa-sort-up ms-1" /> : <i className="fa fa-sort-down ms-1" />;
    };

    const startIdx = (page - 1) * perPage;

    return (
        <div className="box-body b-white rounded-2">
            {/* ── Header ── */}
            <div className="d-flex align-items-center justify-content-between p-2 border-bottom">
                <div className="d-flex align-items-center gap-2">
                    <i className="fa fa-archive text-secondary" style={{ fontSize: '18px' }} />
                    <h5 className="m-0">Sales Orders Archive</h5>
                    {!loading && totalElements > 0 && (
                        <span className="badge bg-secondary rounded-pill">{totalElements.toLocaleString()} records</span>
                    )}
                </div>
                <div className="d-flex gap-2">
                    {hasActiveFilters && (
                        <button className="btn btn-sm btn-outline-danger" onClick={clearFilters}>
                            <i className="fa fa-times me-1" />Clear Filters
                        </button>
                    )}
                    <button
                        className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setShowFilters(v => !v)}
                    >
                        <i className="fa fa-sliders me-1" />
                        Advanced Filters
                        {hasActiveFilters && <span className="ms-1 badge bg-danger rounded-pill" style={{ fontSize: '10px' }}>!</span>}
                    </button>
                </div>
            </div>

            {/* ── Advanced Filter Panel ── */}
            {showFilters && (
                <div className="border-bottom p-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="row g-2">
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Order #</label>
                            <input
                                className="form-control form-control-sm"
                                placeholder="Exact order number"
                                value={orderSearch}
                                onChange={e => setOrderSearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Customer PO #</label>
                            <input
                                className="form-control form-control-sm"
                                placeholder="Exact PO number"
                                value={poSearch}
                                onChange={e => setPoSearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Customer</label>
                            <input
                                className="form-control form-control-sm"
                                placeholder="Customer name"
                                value={customerSearch}
                                onChange={e => setCustomerSearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Part #</label>
                            <input
                                className="form-control form-control-sm"
                                placeholder="Part number"
                                value={partName}
                                onChange={e => setPartName(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Status</label>
                            <input
                                className="form-control form-control-sm"
                                placeholder="Order status"
                                value={statusSearch}
                                onChange={e => setStatusSearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Ship Method</label>
                            <input
                                className="form-control form-control-sm"
                                placeholder="Shipping method"
                                value={shipMethodSearch}
                                onChange={e => setShipMethodSearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Country</label>
                            <input
                                className="form-control form-control-sm"
                                placeholder="Country"
                                value={countrySearch}
                                onChange={e => setCountrySearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Due Date From</label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={startDate}
                                onChange={e => { setStartDate(e.target.value); setPage(1); }}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-semibold mb-1">Due Date To</label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={endDate}
                                onChange={e => { setEndDate(e.target.value); setPage(1); }}
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-end gap-1">
                            <button className="btn btn-sm btn-primary flex-grow-1" onClick={applySearch}>
                                <i className="fa fa-search me-1" />Search
                            </button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters} title="Reset all">
                                <i className="fa fa-refresh" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Toolbar ── */}
            <div className="d-flex align-items-center justify-content-between px-2 py-2">
                <div className="d-flex align-items-center gap-2">
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 'auto' }}
                        value={perPage}
                        onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                    >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    <span className="text-muted small">per page</span>
                </div>
                <div className="d-flex gap-1" style={{ width: '380px' }}>
                    <div className="input-group input-group-sm flex-grow-1">
                        <span className="input-group-text bg-white">
                            <i className="fa fa-search text-muted" />
                        </span>
                        <input
                            type="text"
                            className="form-control border-start-0"
                            placeholder="Search part, order #, customer PO, status, customer..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        {search && (
                            <button className="btn btn-outline-secondary" onClick={() => { setSearch(''); setApplied(a => ({ ...a, search: '' })); }}>
                                <i className="fa fa-times" />
                            </button>
                        )}
                    </div>
                    <button className="btn btn-sm btn-primary px-3" onClick={applySearch}>
                        Search
                    </button>
                </div>
            </div>

            {/* ── Error ── */}
            {error && (
                <div className="mx-2 mb-2 alert alert-danger py-2 d-flex align-items-center gap-2">
                    <i className="fa fa-exclamation-triangle" />
                    {error}
                    <button className="btn btn-sm btn-outline-danger ms-auto" onClick={applySearch}>Retry</button>
                </div>
            )}

            {/* ── Toast ── */}
            {toast && (
                <div className={`mx-2 mb-2 alert alert-${toast.type === 'success' ? 'success' : 'danger'} py-2 d-flex align-items-center gap-2`}>
                    <i className={`fa ${toast.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`} />
                    {toast.message}
                    <button type="button" className="btn-close ms-auto" style={{ fontSize: '12px' }} onClick={() => setToast(null)} />
                </div>
            )}

            {/* ── Table ── */}
            <div className="px-2">
                <div className="table-responsive bridge-manual-scroll" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                    <table className="table table-bordered table-hover table-sm mb-0">
                        <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr>
                                <th className="text-center" style={{ minWidth: '46px', width: '46px' }}>View</th>
                                <th className="text-center" style={{ minWidth: '110px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('ordernum')}>
                                    Order # <SortIcon col="ordernum" />
                                </th>
                                <th className="text-center" style={{ minWidth: '180px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('custname')}>
                                    Customer <SortIcon col="custname" />
                                </th>
                                <th className="text-center" style={{ minWidth: '110px' }}>PO #</th>
                                <th className="text-center" style={{ minWidth: '120px' }}>Part #</th>
                                <th className="text-center" style={{ minWidth: '130px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('orig_shipdate')}>
                                    Ship Date <SortIcon col="orig_shipdate" />
                                </th>
                                <th className="text-center" style={{ minWidth: '65px' }}>Lines</th>
                                <th className="text-center" style={{ minWidth: '130px' }}>Ship Method</th>
                                <th className="text-center" style={{ minWidth: '110px' }}>Country</th>
                                <th className="text-center" style={{ minWidth: '180px' }}>Shipping Notes</th>
                                <th className="text-center" style={{ minWidth: '200px' }}>Comments</th>
                                <th className="text-center" style={{ minWidth: '110px', width: '110px' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="12" className="text-center py-5">
                                        <div className="spinner-border text-secondary spinner-border-sm me-2" role="status" />
                                        <span className="text-muted">Loading archived orders...</span>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan="12" className="text-center text-muted py-5">
                                        <i className="fa fa-archive fa-2x mb-2 d-block" style={{ color: '#dee2e6' }} />
                                        {hasActiveFilters ? 'No archived orders match the current filters.' : 'No archived orders found.'}
                                    </td>
                                </tr>
                            ) : (
                                data.map((line, i) => (
                                    <tr key={`arch-so-${line.id ?? i}`} className={getRowClass(line.shipdate || line.orig_shipdate)}>
                                        <td className="text-center align-middle">
                                            <a href={`https://gigaflightinc.cetecerp.com/order/MN${line.order?.ordernum}/view`} target="_blank" rel="noopener noreferrer" title="View in ERP">
                                                <img src={viewIcon} style={{ width: '18px' }} alt="View" />
                                            </a>
                                        </td>
                                        <td className="text-center align-middle">
                                            <div className="d-flex align-items-center justify-content-center gap-1">
                                                <a href={`https://gigaflightinc.cetecerp.com/order/MN${line.order?.ordernum}/view`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', fontWeight: 500 }}>
                                                    {line.order?.ordernum || 'N/A'}
                                                </a>
                                                <i className="fa fa-folder" style={{ fontSize: '13px', color: '#ff8c00' }} />
                                            </div>
                                        </td>
                                        <td className="text-center align-middle">
                                            {line.order?.customer?.id ? (
                                                <a href={`https://gigaflightinc.cetecerp.com/customer/${line.order.customer.id}/view`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                                    {line.order?.customer?.name || 'N/A'}
                                                </a>
                                            ) : (line.order?.customer?.name || 'N/A')}
                                        </td>
                                        <td className="text-center align-middle">{line.order?.custponum || 'N/A'}</td>
                                        <td className="text-center align-middle">
                                            <span title={line.prcpart || line.part?.prcpart || ''}>
                                                {line.prcpart ? line.prcpart.substring(3) : (line.part?.prcpart || 'N/A')}
                                            </span>
                                        </td>
                                        <td className="text-center align-middle">{line.orig_shipdate || 'N/A'}</td>
                                        <td className="text-center align-middle">{line.linesIds?.length ?? line.negativeCountStr ?? '-'}</td>
                                        <td className="text-center align-middle">{line.order?.shipmentMethod?.methodOfShipment || 'N/A'}</td>
                                        <td className={`text-center align-middle ${line.order?.country?.country_Name && line.order.country.country_Name !== 'United States' ? 'bg-dark text-light country-cell-intl' : (line.order?.country?.country_Name === 'United States' ? 'country-cell-us' : '')}`}>
                                            {line.order?.country?.country_Name || 'N/A'}
                                        </td>
                                        <td className="text-center align-middle">
                                            <div dangerouslySetInnerHTML={{ __html: line.order?.shipping_instructions || '-' }}
                                                style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} />
                                        </td>
                                        <td className="text-center align-middle">
                                            {Array.isArray(line.comment) && line.comment.length > 0 ? (
                                                <div title={line.comment[line.comment.length - 1]?.comment}
                                                    style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {line.comment[line.comment.length - 1]?.comment || '-'}
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td className="text-center align-middle">
                                            {(() => {
                                                const orderNum = line.order?.ordernum;
                                                const busy = unarchivingIds.has(orderNum);
                                                return (
                                                    <button
                                                        className="btn btn-sm btn-outline-warning"
                                                        style={{ fontSize: '11px', whiteSpace: 'nowrap' }}
                                                        disabled={busy}
                                                        onClick={() => handleUnarchive(line)}
                                                        title={`Unarchive order #${orderNum}`}
                                                    >
                                                        {busy
                                                            ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Restoring...</>
                                                            : <><i className="fa fa-undo me-1" />Unarchive</>
                                                        }
                                                    </button>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Pagination ── */}
            {!loading && totalPages > 0 && (
                <div className="d-flex justify-content-between align-items-center px-2 py-2 border-top">
                    <span className="text-muted small">
                        Showing <strong>{startIdx + 1}</strong>–<strong>{Math.min(startIdx + perPage, totalElements)}</strong> of{' '}
                        <strong>{totalElements.toLocaleString()}</strong> archived orders
                    </span>
                    <ul className="pagination pagination-sm mb-0 align-items-center">
                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(1)} disabled={page === 1}>«</button>
                        </li>
                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(p => p - 1)} disabled={page === 1}>Previous</button>
                        </li>
                        {totalPages > 1 && (
                            <li className="page-item">
                                <select className="form-select form-select-sm mx-2" style={{ width: 'auto' }} value={page} onChange={e => setPage(Number(e.target.value))}>
                                    {[...Array(totalPages)].map((_, i) => (
                                        <option key={i} value={i + 1}>Page {i + 1} of {totalPages}</option>
                                    ))}
                                </select>
                            </li>
                        )}
                        <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>Next</button>
                        </li>
                        <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
};

export default SalesOrdersArchive;
